import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Scissors, Bath, ArrowLeft, Search, User, PawPrint, CalendarDays, Clock3, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import appApi from "./appApi";
import { Skeleton, spring } from "./bits";
import { AppCalendar, TimeWheel } from "./pickers";
import { loadPlanPrices } from "@/lib/plans";
import { normalizePhone, isValidGrPhone } from "@/lib/phone";

const SIZES = ["small", "medium", "large", "giant"];

const StepTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand"><Icon className="h-5 w-5" /></div>
    <h2 className="font-heading text-2xl font-medium text-brand-ink">{children}</h2>
  </div>
);

const ChoiceCard = ({ active, onClick, children, testid }) => (
  <motion.button
    type="button"
    whileTap={{ scale: 0.96 }}
    transition={spring}
    onClick={onClick}
    className={`w-full text-left rounded-2xl border p-4 transition-colors ${active ? "border-brand bg-brand-soft/50 shadow-[0_10px_30px_rgba(134,77,99,0.15)]" : "border-brand/10 bg-white"}`}
    data-testid={testid}
  >
    {children}
  </motion.button>
);

const inputCls = "w-full h-12 rounded-xl border border-brand/20 bg-white px-4 text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand/40";

const WizardScreen = ({ onDone }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [step, setStep] = useState(0);
  const [type, setType] = useState(null); // grooming | selfwash
  const [mode, setMode] = useState("existing");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", pet_name: "", pet_breed: "", pet_size: "medium", plan: "medium", nagayu: false, notes: "" });
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [plans, setPlans] = useState(null);

  useEffect(() => { loadPlanPrices().then(setPlans); }, []);

  // existing customer search (debounced)
  useEffect(() => {
    if (mode !== "existing" || query.trim().length < 2) { setResults(null); return; }
    const tmr = setTimeout(async () => {
      try {
        const res = await appApi.get(`/app/customers/search?q=${encodeURIComponent(query.trim())}`);
        setResults(res.data);
      } catch { setResults([]); }
    }, 280);
    return () => clearTimeout(tmr);
  }, [query, mode]);

  const pickCustomer = (c) => {
    setPicked(c);
    setForm((f) => ({
      ...f,
      customer_name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      pet_name: c.pets?.[0] || "",
      pet_breed: c.breeds?.[0] || "",
    }));
  };

  const canNext = () => {
    if (step === 0) return !!type;
    if (step === 1) return mode === "existing" ? !!picked : form.customer_name.trim().length > 1 && isValidGrPhone(normalizePhone(form.phone));
    if (step === 2 && type === "grooming") return form.pet_name.trim().length > 0 && form.pet_breed.trim().length > 0;
    if (step === 2 && type === "selfwash") return !!form.plan;
    if (step === 3) return !!date;
    if (step === 4) return !!time;
    return true;
  };

  const stepsTotal = 6;

  const submit = async () => {
    setBusy(true);
    try {
      const phone = normalizePhone(form.phone);
      if (type === "grooming") {
        await appApi.post("/app/bookings/grooming", {
          customer_name: form.customer_name.trim(), phone, email: form.email.trim() || null,
          pet_name: form.pet_name.trim(), pet_breed: form.pet_breed.trim(), pet_size: form.pet_size,
          preferred_date: date, preferred_time: time, notes: form.notes.trim() || null,
        });
      } else {
        const plan_price = plans?.[form.plan] ?? 14;
        await appApi.post("/app/bookings/selfwash", {
          customer_name: form.customer_name.trim(), phone, email: form.email.trim() || null,
          plan: form.plan, plan_price, plan_duration: 30, nagayu: form.nagayu,
          date, time, notes: form.notes.trim() || null,
        });
      }
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || m.error_generic);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={spring}
        className="flex flex-col items-center text-center pt-16" data-testid="wizard-success"
      >
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 12, delay: 0.1 }}
          className="grid place-items-center h-24 w-24 rounded-full bg-brand text-brand-cream shadow-[0_20px_50px_rgba(134,77,99,0.4)]"
        >
          <PartyPopper className="h-11 w-11" />
        </motion.div>
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.span
            key={i}
            className="absolute text-brand"
            initial={{ opacity: 0, y: 0, x: 0 }}
            animate={{ opacity: [0, 1, 0], y: [-20, -90], x: [0, (i - 2) * 46] }}
            transition={{ delay: 0.3 + i * 0.08, duration: 1.1 }}
          >
            <PawPrint className="h-5 w-5" />
          </motion.span>
        ))}
        <h2 className="mt-6 font-heading text-3xl font-medium text-brand-ink">{m.w_success}</h2>
        <p className="mt-2 text-brand-ink/60">{form.customer_name} · {date} · {time}</p>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onDone}
          className="mt-8 rounded-full bg-brand text-brand-cream font-bold px-8 h-12 shadow-lg"
          data-testid="wizard-view-today"
        >
          {m.w_view_today}
        </motion.button>
      </motion.div>
    );
  }

  return (
    <div data-testid="wizard-screen">
      <div className="flex items-center gap-3 mb-6">
        {step > 0 && (
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => setStep(step - 1)} className="grid place-items-center h-10 w-10 rounded-full bg-white border border-brand/15 text-brand" data-testid="wizard-back">
            <ArrowLeft className="h-5 w-5" />
          </motion.button>
        )}
        <div className="flex-1 flex gap-1.5">
          {Array.from({ length: stepsTotal }).map((_, i) => (
            <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand" : "bg-brand/15"}`} />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={spring}
        >
          {step === 0 && (
            <>
              <StepTitle icon={Scissors}>{m.w_choose_type}</StepTitle>
              <div className="grid gap-3">
                <ChoiceCard active={type === "grooming"} onClick={() => { setType("grooming"); setStep(1); }} testid="wizard-type-grooming">
                  <div className="flex items-center gap-3">
                    <div className="grid place-items-center h-12 w-12 rounded-xl bg-brand-soft text-brand"><Scissors className="h-6 w-6" /></div>
                    <div><p className="font-bold text-brand-ink text-lg">{m.w_grooming}</p><p className="text-xs text-brand-ink/55">{m.w_grooming_sub}</p></div>
                  </div>
                </ChoiceCard>
                <ChoiceCard active={type === "selfwash"} onClick={() => { setType("selfwash"); setStep(1); }} testid="wizard-type-selfwash">
                  <div className="flex items-center gap-3">
                    <div className="grid place-items-center h-12 w-12 rounded-xl bg-sky-50 text-sky-600"><Bath className="h-6 w-6" /></div>
                    <div><p className="font-bold text-brand-ink text-lg">{m.w_selfwash}</p><p className="text-xs text-brand-ink/55">{m.w_selfwash_sub}</p></div>
                  </div>
                </ChoiceCard>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <StepTitle icon={User}>{m.w_customer}</StepTitle>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <ChoiceCard active={mode === "existing"} onClick={() => setMode("existing")} testid="wizard-mode-existing"><p className="font-bold text-center">{m.w_existing}</p></ChoiceCard>
                <ChoiceCard active={mode === "new"} onClick={() => setMode("new")} testid="wizard-mode-new"><p className="font-bold text-center">{m.w_new}</p></ChoiceCard>
              </div>
              {mode === "existing" ? (
                <div>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand/40" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={m.w_search_ph} className={`${inputCls} pl-12`} data-testid="wizard-search" />
                  </div>
                  <div className="mt-3 space-y-2">
                    {results === null && query.trim().length >= 2 && <Skeleton className="h-16" />}
                    {results && results.length === 0 && <p className="text-sm text-brand-ink/40 italic py-4 text-center">{m.w_no_results}</p>}
                    {results?.slice(0, 6).map((c) => (
                      <ChoiceCard key={c.phone} active={picked?.phone === c.phone} onClick={() => pickCustomer(c)} testid={`wizard-pick-${c.phone}`}>
                        <p className="font-bold text-brand-ink">{c.name}</p>
                        <p className="text-xs text-brand-ink/55">{c.phone}{c.pets?.length ? ` · ${c.pets.join(", ")}` : ""}</p>
                      </ChoiceCard>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder={m.w_name} className={inputCls} data-testid="wizard-name" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={m.w_phone} inputMode="tel" className={inputCls} data-testid="wizard-phone" />
                  <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={m.w_email} inputMode="email" className={inputCls} data-testid="wizard-email" />
                </div>
              )}
            </>
          )}

          {step === 2 && type === "grooming" && (
            <>
              <StepTitle icon={PawPrint}>{m.w_pet_step}</StepTitle>
              <div className="space-y-3">
                <input value={form.pet_name} onChange={(e) => setForm({ ...form, pet_name: e.target.value })} placeholder={m.w_pet_name} className={inputCls} data-testid="wizard-pet-name" />
                <input value={form.pet_breed} onChange={(e) => setForm({ ...form, pet_breed: e.target.value })} placeholder={m.w_pet_breed} className={inputCls} data-testid="wizard-pet-breed" />
                <div className="grid grid-cols-2 gap-2">
                  {SIZES.map((sz) => (
                    <ChoiceCard key={sz} active={form.pet_size === sz} onClick={() => setForm({ ...form, pet_size: sz })} testid={`wizard-size-${sz}`}>
                      <p className="font-bold text-center text-sm">{m[`size_${sz}`]}</p>
                    </ChoiceCard>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && type === "selfwash" && (
            <>
              <StepTitle icon={Bath}>{m.w_plan_step}</StepTitle>
              <div className="grid grid-cols-2 gap-2">
                {SIZES.map((sz) => (
                  <ChoiceCard key={sz} active={form.plan === sz} onClick={() => setForm({ ...form, plan: sz })} testid={`wizard-plan-${sz}`}>
                    <p className="font-bold text-center">{m[`size_${sz}`]}</p>
                    <p className="text-center text-brand font-bold mt-1">€{plans?.[sz] ?? "…"}</p>
                  </ChoiceCard>
                ))}
              </div>
              <ChoiceCard active={form.nagayu} onClick={() => setForm({ ...form, nagayu: !form.nagayu })} testid="wizard-nagayu">
                <p className="font-bold text-sm mt-3">{m.w_nagayu}</p>
              </ChoiceCard>
            </>
          )}

          {step === 3 && (
            <>
              <StepTitle icon={CalendarDays}>{m.w_date_step}</StepTitle>
              <AppCalendar value={date} onChange={setDate} />
            </>
          )}

          {step === 4 && (
            <>
              <StepTitle icon={Clock3}>{m.w_time_step}</StepTitle>
              <TimeWheel value={time} onChange={setTime} />
              <p className="mt-3 text-center text-xs text-brand-ink/50">{m.w_any_time}</p>
            </>
          )}

          {step === 5 && (
            <>
              <StepTitle icon={PartyPopper}>{m.w_confirm}</StepTitle>
              <div className="rounded-2xl bg-white border border-brand/10 p-5 shadow-sm space-y-2 text-sm" data-testid="wizard-summary">
                <p className="font-bold text-brand-ink text-base">{type === "grooming" ? m.w_grooming : m.w_selfwash}</p>
                <p><span className="text-brand-ink/50">{m.w_customer}</span> {form.customer_name} · {normalizePhone(form.phone)}</p>
                {type === "grooming" && <p><span className="text-brand-ink/50">{m.w_pet_name}</span> {form.pet_name} · {form.pet_breed} · {m[`size_${form.pet_size}`]}</p>}
                {type === "selfwash" && <p><span className="text-brand-ink/50">{m.w_plan_step}</span> {m[`size_${form.plan}`]} €{plans?.[form.plan]}{form.nagayu ? " + Nagayu" : ""}</p>}
                <p className="font-bold text-brand">{date} · {time}</p>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={m.w_notes} className="mt-2 w-full rounded-xl border border-brand/20 bg-white px-4 py-3 text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand/40 min-h-[70px]" data-testid="wizard-notes" />
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {step > 0 && (
        <motion.button
          whileTap={{ scale: 0.97 }}
          disabled={!canNext() || busy}
          onClick={() => (step < 5 ? setStep(step + 1) : submit())}
          className="mt-8 w-full h-14 rounded-2xl bg-brand text-brand-cream font-bold text-lg shadow-[0_12px_30px_rgba(134,77,99,0.35)] disabled:opacity-40"
          data-testid="wizard-next"
        >
          {busy ? m.loading : step === 5 ? m.w_confirm_btn : m.w_next}
        </motion.button>
      )}
    </div>
  );
};

export default WizardScreen;
